/**
 * Dialogblatt – der eine Ort für alle überlagerten Fenster der App.
 *
 * Zwei Formen, je nach Bildschirm, und das ist Absicht:
 *
 *   schmal (Handy)   Blatt von unten, oben bleibt ein Streifen Hintergrund
 *                    sichtbar. Unten, weil der Daumen dort hinkommt.
 *   breit (Rechner)  freistehendes Fenster in der Mitte, mit Abstand nach
 *                    allen Seiten. Der Hintergrund bleibt ringsum sichtbar,
 *                    damit erkennbar ist, wo man sich befindet.
 *
 * Alles Weitere gehört ebenfalls hierher und nicht in die einzelnen Seiten:
 * Schließen per Escape und per Klick daneben, Sperre des Hintergrundlaufs,
 * Fokus ins Blatt und zurück, sowie die Rollen für die Sprachausgabe.
 */

import { useEffect, useRef, type ReactNode } from 'react'

type Eigenschaften = {
  /** Überschrift des Blattes. Steht auch in der Beschriftung für die Sprachausgabe. */
  titel: string
  untertitel?: ReactNode
  schliessen: () => void
  children: ReactNode
  /** Feste Fußzeile, bleibt beim Blättern stehen – dort liegen die Knöpfe. */
  fuss?: ReactNode
  /** Breiter für Blätter mit viel Inhalt. */
  breit?: boolean
}

export function Blatt({
  titel,
  untertitel,
  schliessen,
  children,
  fuss,
  breit = false,
}: Eigenschaften) {
  const fenster = useRef<HTMLDivElement>(null)
  const vorherFokussiert = useRef<HTMLElement | null>(null)

  useEffect(() => {
    vorherFokussiert.current = document.activeElement as HTMLElement | null
    fenster.current?.focus()

    // Hintergrund nicht mitlaufen lassen. Ohne das scrollt beim Wischen im
    // Blatt die Seite darunter weg, und nach dem Schließen steht man an einer
    // anderen Stelle des Prüfbogens.
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const taste = (ereignis: KeyboardEvent) => {
      if (ereignis.key === 'Escape') {
        ereignis.stopPropagation()
        schliessen()
      }
    }
    window.addEventListener('keydown', taste)

    return () => {
      document.body.style.overflow = vorher
      window.removeEventListener('keydown', taste)
      // Fokus dorthin zurück, wo er war – sonst springt er an den Seitenanfang.
      vorherFokussiert.current?.focus?.()
    }
  }, [schliessen])

  return (
    <div className="fixed inset-0 z-40">
      {/* Abdunklung. Ein Klick darauf schließt. */}
      <button
        type="button"
        aria-label="Blatt schließen"
        onClick={schliessen}
        className="absolute inset-0 h-full w-full cursor-default bg-slate-900/45"
      />

      {/* Auf dem Handy unten angeschlagen, auf dem Rechner mittig und
          freistehend. `pointer-events-none` am Rahmen, damit die Abdunklung
          darunter klickbar bleibt. */}
      <div
        className="pointer-events-none absolute inset-0 flex justify-center
                   items-end p-0 sm:items-center sm:p-6"
      >
        <div
          ref={fenster}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={titel}
          className={`pointer-events-auto flex w-full flex-col overflow-hidden bg-white
                      shadow-2xl outline-none
                      max-h-[88dvh] rounded-t-2xl border-t-4 blatt-kante
                      sm:max-h-[85dvh] sm:rounded-2xl sm:border-4
                      ${breit ? 'sm:max-w-4xl' : 'sm:max-w-2xl'}`}
        >
          <header className="flex items-start gap-3 border-b-2 border-slate-200 p-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold">{titel}</h2>
              {untertitel ? (
                <div className="text-base text-slate-700">{untertitel}</div>
              ) : null}
            </div>
            <button type="button" className="knopf-still px-3" onClick={schliessen}>
              Schließen
            </button>
          </header>

          {/* Nur dieser Bereich läuft, damit Kopf und Fuß stehen bleiben. */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

          {fuss ? (
            <footer
              className="border-t-2 border-slate-200 bg-white p-4"
              style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
            >
              {fuss}
            </footer>
          ) : null}
        </div>
      </div>
    </div>
  )
}
